import { filterClasses } from "@swivl-mono/helpers";
import { FeatureFlagKey } from "@swivl-mono/types";
import { IconNotifications, IconProfile, IconSecurity, IconWorkingHours, Modal, ModalSize } from "@swivl-mono/ui";
import { ComponentType, ReactNode, useState } from "react";
import { FiActivity, FiHeadphones } from "react-icons/fi";

import { isFeatureEnabled } from "../../../../../Helpers/FeatureFlags/isFeatureEnabled";
import { isPulseEnabled } from "../../../../../Helpers/FeatureFlags/isPulseEnabled";
import ModalModel from "../../../../../Models/Modal/Modal.Model";
import { AccountSettingsTab } from "./AccountSettingsTabs";
import { AgentSettingsTab } from "./AgentSettingsTab";
import { NotificationsTab } from "./NotificationsTab";
import { ProfileTab } from "./ProfileTab";
import { PulseTab } from "./PulseTab";
import { SecurityTab } from "./SecurityTab";
import { WorkingHoursTab } from "./WorkingHoursTab";

interface TabConfig {
  label: AccountSettingsTab;
  icon: ComponentType<React.SVGProps<SVGSVGElement>>;
}

const TABS: TabConfig[] = [
  { label: AccountSettingsTab.PROFILE, icon: IconProfile },
  { label: AccountSettingsTab.SECURITY, icon: IconSecurity },
  { label: AccountSettingsTab.NOTIFICATIONS, icon: IconNotifications },
  { label: AccountSettingsTab.PULSE, icon: FiActivity },
  // { label: AccountSettingsTab.WORKING_HOURS, icon: IconWorkingHours }
  { label: AccountSettingsTab.AGENT_SETTINGS, icon: FiHeadphones }
];

/** The Agent Settings tab is Contact Center v3 — Snowy orgs only. The Pulse tab is for viewers who get Pulse. */
function visibleTabs(): TabConfig[] {
  return TABS.filter(
    (tab) =>
      (tab.label !== AccountSettingsTab.AGENT_SETTINGS || isFeatureEnabled(FeatureFlagKey.Snowy)) &&
      (tab.label !== AccountSettingsTab.PULSE || isPulseEnabled())
  );
}

interface AccountSettingsModalProps {
  startOnTab?: AccountSettingsTab;
}

export function AccountSettingsModal({ startOnTab }: AccountSettingsModalProps): React.ReactNode {
  const [activeTab, setActiveTab] = useState<AccountSettingsTab>(startOnTab ?? AccountSettingsTab.PROFILE);

  function renderTabContent(tab: AccountSettingsTab): ReactNode {
    switch (tab) {
      case AccountSettingsTab.NOTIFICATIONS:
        return <NotificationsTab />;
      case AccountSettingsTab.PULSE:
        return <PulseTab />;
      case AccountSettingsTab.PROFILE:
        return <ProfileTab />;
      case AccountSettingsTab.SECURITY:
        return <SecurityTab />;
      case AccountSettingsTab.WORKING_HOURS:
        return <WorkingHoursTab />;
      case AccountSettingsTab.AGENT_SETTINGS:
        return <AgentSettingsTab />;
      default:
        return tab;
    }
  }

  return (
    <Modal isOpen={true} onClose={() => ModalModel.hideModal()} title="Account Settings" size={ModalSize.EXTRA_LARGE} hasTitleBorder>
      <div className="flex h-[80vh] max-h-[600px]">
        <div className="min-w-[200px] border-r border-grey-200 py-2">
          <nav className="flex flex-col gap-0.5">
            {visibleTabs().map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.label;

              return (
                <button
                  key={tab.label}
                  className={filterClasses(
                    "flex items-center gap-2.5 border-l-2 px-3 py-2.5 text-grey-700 transition-all duration-150",
                    isActive ? "border-orange-500 bg-orange-50 text-orange-500" : "cursor-pointer border-transparent bg-transparent hover:bg-grey-100"
                  )}
                  onClick={() => setActiveTab(tab.label)}
                >
                  <Icon className={filterClasses("size-4", isActive ? "text-orange-500" : "text-black")} />
                  <span className={isActive ? "body-sm-alt text-orange-500" : "body-sm text-black"}>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        <div className="w-[600px] flex-1 overflow-y-auto p-4">{renderTabContent(activeTab)}</div>
      </div>
    </Modal>
  );
}

export function openAccountSettings(startOnTab?: AccountSettingsTab): void {
  ModalModel.showModalWithContent(null, <AccountSettingsModal startOnTab={startOnTab} />, undefined, undefined, true, false);
}
